import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

/**
 * Closes the summary popover an editor was opened from. A no-op by default: the same
 * editors also render in the chat, with no popover above them.
 */
const CloseSummaryPopoverContext = createContext<() => void>(() => {})

export function SummaryPopoverProvider({ close, children }: { close: () => void; children: ReactNode }) {
  return <CloseSummaryPopoverContext.Provider value={close}>{children}</CloseSummaryPopoverContext.Provider>
}

/**
 * Open state for a modal opened from inside a summary popover. Dismissing the modal takes
 * the popover with it — it's one edit, and leaving the popover behind stranded a dropdown
 * over the page that the customer had no reason to expect.
 */
export function useSummaryEditor() {
  const closePopover = useContext(CloseSummaryPopoverContext)
  const [isOpen, setIsOpen] = useState(false)

  const setOpen = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      if (!next) closePopover()
    },
    [closePopover]
  )

  return {
    isOpen,
    open: useCallback(() => setOpen(true), [setOpen]),
    close: useCallback(() => setOpen(false), [setOpen]),
    setOpen,
  }
}
